'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

// Ad Campaign Interface
interface AdCampaign {
  id: number;
  name: string;
  advertiser: string;
  zone: string;
  status: 'active' | 'paused' | 'scheduled' | 'ended';
  priority: number;
  startDate: string;
  endDate: string;
  creative: {
    type: 'banner' | 'text' | 'rich_media';
    image?: string;
    alt?: string;
    title: string;
    description: string;
    cta: string;
    clickUrl: string;
  };
  targeting: {
    pages?: string[];
    demographics?: string[];
    locations?: string[];
  };
  rotationWeight: number;
  impressions: number;
  clicks: number;
  dailyBudget?: number;
  spent?: number;
}

// Ad Context Interface
interface AdContextType {
  getAdsForZone: (zone: string, currentPage?: string) => AdCampaign[];
  trackImpression: (adId: number, zone: string) => void;
  trackClick: (adId: number, zone: string) => void;
  adCampaigns: AdCampaign[];
}

// Create Context
const AdContext = createContext<AdContextType | undefined>(undefined);

// Mock Ad Data - In production, this would come from your database/API
const mockAdCampaigns: AdCampaign[] = [
{
  id: 1,
  name: 'Caterpillar Equipment Showcase Q4 2025',
  advertiser: 'Caterpillar Inc.',
  zone: 'homepage_leaderboard',
  status: 'active',
  priority: 1,
  startDate: '2025-11-01',
  endDate: '2025-12-31',
  creative: {
    type: 'banner',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa4ebf85-1765103683189.png",
    alt: 'Caterpillar excavator showcase banner featuring CAT 320 model with advanced hydraulics',
    title: 'New CAT 320 Excavator Series',
    description: 'Advanced hydraulics, superior fuel efficiency, unmatched performance',
    cta: 'Learn More',
    clickUrl: 'https://www.cat.com'
  },
  targeting: {
    pages: ['home', 'marketplace-listings', 'equipment-detail'],
    demographics: ['Construction Managers', 'Equipment Operators']
  },
  rotationWeight: 70,
  impressions: 245680,
  clicks: 4912,
  dailyBudget: 500,
  spent: 8750
},
{
  id: 2,
  name: 'United Rentals Holiday Promotion',
  advertiser: 'United Rentals',
  zone: 'sidebar_mpu',
  status: 'active',
  priority: 2,
  startDate: '2025-11-15',
  endDate: '2025-12-31',
  creative: {
    type: 'rich_media',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb4a62e5-1767718945068.png",
    alt: 'United Rentals holiday promotion banner with seasonal equipment rental offers',
    title: 'Holiday Equipment Rentals',
    description: 'Special rates on construction equipment through December. Quick delivery available.',
    cta: 'Get Quote',
    clickUrl: 'https://www.unitedrentals.com'
  },
  targeting: {
    pages: ['marketplace-listings', 'equipment-detail', 'jobs-board'],
    demographics: ['Project Managers', 'Site Supervisors']
  },
  rotationWeight: 60,
  impressions: 89430,
  clicks: 1788,
  dailyBudget: 350,
  spent: 4250
},
{
  id: 3,
  name: 'John Deere Construction Launch',
  advertiser: 'John Deere Construction',
  zone: 'article_inline_p2',
  status: 'active',
  priority: 1,
  startDate: '2025-10-15',
  endDate: '2026-01-15',
  creative: {
    type: 'banner',
    image: "https://images.unsplash.com/photo-1708098473162-b4685f85269c",
    alt: 'John Deere construction equipment launch campaign featuring new excavator models',
    title: 'John Deere Construction Equipment',
    description: 'Discover the new line of construction equipment built for efficiency and durability',
    cta: 'Shop Now',
    clickUrl: 'https://www.deere.com/construction'
  },
  targeting: {
    pages: ['news-hub', 'article-detail'],
    demographics: ['Equipment Buyers', 'Fleet Managers']
  },
  rotationWeight: 80,
  impressions: 156320,
  clicks: 3126,
  dailyBudget: 600,
  spent: 12450
},
{
  id: 4,
  name: 'Volvo Equipment Finance',
  advertiser: 'Volvo Construction Equipment',
  zone: 'global_leaderboard',
  status: 'active',
  priority: 2,
  startDate: '2025-11-01',
  endDate: '2025-12-15',
  creative: {
    type: 'text',
    title: 'Volvo Equipment Financing',
    description: '0% APR for 12 months on select construction equipment. Apply today for instant approval.',
    cta: 'Apply Now',
    clickUrl: 'https://www.volvoce.com'
  },
  targeting: {
    pages: ['marketplace-listings', 'equipment-detail', 'jobs-board'],
    demographics: ['Equipment Buyers', 'Business Owners']
  },
  rotationWeight: 50,
  impressions: 78920,
  clicks: 1578,
  dailyBudget: 300,
  spent: 5670
},
{
  id: 5,
  name: 'Heavy Equipment Insurance',
  advertiser: 'National Equipment Insurance',
  zone: 'sidebar_mpu',
  status: 'active',
  priority: 3,
  startDate: '2025-11-10',
  endDate: '2025-12-31',
  creative: {
    type: 'rich_media',
    image: "https://images.unsplash.com/photo-1728922311668-a279c5562e7c",
    alt: 'Heavy equipment insurance banner showing construction site with protected machinery',
    title: 'Equipment Insurance Coverage',
    description: 'Protect your valuable machinery with comprehensive insurance. Get a quote in minutes.',
    cta: 'Get Quote',
    clickUrl: 'https://www.nationalequipmentinsurance.com'
  },
  targeting: {
    pages: ['equipment-detail', 'marketplace-listings'],
    demographics: ['Equipment Owners', 'Fleet Managers']
  },
  rotationWeight: 40,
  impressions: 45680,
  clicks: 913,
  dailyBudget: 200,
  spent: 2850
},
{
  id: 6,
  name: 'Construction Job Board Ads',
  advertiser: 'TechHire Staffing',
  zone: 'job_grid_inline',
  status: 'active',
  priority: 2,
  startDate: '2025-11-01',
  endDate: '2025-11-30',
  creative: {
    type: 'text',
    title: 'Skilled Equipment Operators Wanted',
    description: 'Join top construction companies. Competitive pay, benefits, and growth opportunities.',
    cta: 'Apply Today',
    clickUrl: 'https://www.techhire.com'
  },
  targeting: {
    pages: ['jobs-board', 'job-detail'],
    demographics: ['Equipment Operators', 'Construction Workers']
  },
  rotationWeight: 65,
  impressions: 67890,
  clicks: 2035,
  dailyBudget: 250,
  spent: 4125
}];


export const AdRotationProvider: React.FC<{children: React.ReactNode;}> = ({ children }) => {
  const [adCampaigns] = useState<AdCampaign[]>(mockAdCampaigns);
  const [impressionCounts, setImpressionCounts] = useState<Record<string, number>>({});

  const getAdsForZone = (zone: string, currentPage?: string): AdCampaign[] => {
    const now = new Date();

    const eligibleAds = adCampaigns.filter((ad) => {
      const startDate = new Date(ad.startDate);
      const endDate = new Date(ad.endDate);

      const isActive = ad.status === 'active';
      const isInDateRange = now >= startDate && now <= endDate;
      const isCorrectZone = ad.zone === zone;

      const pageMatches = !ad.targeting.pages || !currentPage ||
      ad.targeting.pages.some((page) => currentPage.includes(page));

      return isActive && isInDateRange && isCorrectZone && pageMatches;
    });

    return eligibleAds.sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return b.rotationWeight - a.rotationWeight;
    });
  };

  const trackImpression = (adId: number, zone: string) => {
    const key = `${adId}-${zone}`;
    setImpressionCounts((prev) => ({
      ...prev,
      [key]: (prev[key] || 0) + 1
    }));

    console.log(`Ad impression tracked: ID ${adId} in zone ${zone}`);
  };

  const trackClick = (adId: number, zone: string) => {
    console.log(`Ad click tracked: ID ${adId} in zone ${zone}`);

    const ad = adCampaigns.find((campaign) => campaign.id === adId);
    if (ad?.creative.clickUrl) {
      window.open(ad.creative.clickUrl, '_blank');
    }
  };

  const contextValue: AdContextType = {
    getAdsForZone,
    trackImpression,
    trackClick,
    adCampaigns
  };

  return (
    <AdContext.Provider value={contextValue}>
      {children}
    </AdContext.Provider>);

};

export const useAdRotation = (): AdContextType => {
  const context = useContext(AdContext);
  if (!context) {
    if (typeof window === 'undefined') {
      return {
        getAdsForZone: () => [],
        trackImpression: () => {},
        trackClick: () => {},
        adCampaigns: []
      };
    }
    throw new Error('useAdRotation must be used within an AdRotationProvider');
  }
  return context;
};

export default AdRotationProvider;
const AdZoneDisplay: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: AdZoneDisplay is not implemented yet.');
  }, []);
  return (
    <div>
      {/* AdZoneDisplay placeholder */}
    </div>);

};

export { AdZoneDisplay };